/**
 * Appointment Display Component
 *
 * Displays appointment information with calendar integration options.
 * Handles loading, error, and no-appointment states.
 *
 * @module components/patient-dashboard/AppointmentDisplay
 */

import React, { useState, useRef } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { SubtitleText } from '@/components/ui/SubtitleText';
import { BodyText } from '@/components/ui/BodyText';
import { Button } from '@/components/ui/Button';
import { PaymentGuidance } from '@/components/payment-guidance';
import type { CalendlyAppointment, CalendlyAppointmentApiError } from '@/types/calendlyAppointments';

/**
 * Props for AppointmentDisplay component
 */
export interface AppointmentDisplayProps {
  /** Appointment data */
  appointment: CalendlyAppointment | null;
  /** Loading state for appointments */
  isLoading: boolean;
  /** Error state for appointments */
  error: CalendlyAppointmentApiError | null;
  /** Callback to retry fetching appointments */
  onRetry?: () => void;
  /** Whether to show action buttons */
  showButtons?: boolean;
  /** Title for the appointment section */
  title?: string;
}

/**
 * Format date and time for display
 */
function formatAppointmentDateTime(startTime: string, endTime: string): {
  date: string;
  time: string;
  dayOfWeek: string;
} {
  const start = new Date(startTime);
  const end = new Date(endTime);

  const dateOptions: Intl.DateTimeFormatOptions = {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const dayOptions: Intl.DateTimeFormatOptions = {
    weekday: 'long',
  };

  return {
    date: start.toLocaleDateString('en-AU', dateOptions),
    time: `${start.toLocaleTimeString('en-AU', timeOptions)} - ${end.toLocaleTimeString('en-AU', timeOptions)}`,
    dayOfWeek: start.toLocaleDateString('en-AU', dayOptions),
  };
}

/**
 * Calculate time until appointment
 */
function getTimeUntilAppointment(startTime: string): string {
  const now = new Date();
  const appointment = new Date(startTime);
  const diffMs = appointment.getTime() - now.getTime();

  if (diffMs <= 0) {
    return 'Starting soon';
  }

  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  if (diffDays > 0) {
    return `In ${diffDays} day${diffDays > 1 ? 's' : ''}`;
  } else if (diffHours > 0) {
    return `In ${diffHours} hour${diffHours > 1 ? 's' : ''}`;
  } else if (diffMinutes > 0) {
    return `In ${diffMinutes} minute${diffMinutes > 1 ? 's' : ''}`;
  } else {
    return 'Starting soon';
  }
}

/**
 * Extract eventId from reschedule URL or cancel URL
 * @param rescheduleUrl - URL in format "https://calendly.com/reschedulings/{eventId}"
 * @param cancelUrl - URL in format "https://calendly.com/cancellations/{eventId}"
 * @returns eventId or null if extraction fails
 */
function extractEventIdFromUrl(rescheduleUrl: string, cancelUrl: string | null): string | null {
  try {
    const url = new URL(rescheduleUrl);
    const pathSegments = url.pathname.split('/');
    // Expected format: /reschedulings/{eventId}
    if (pathSegments.length >= 3 && pathSegments[1] === 'reschedulings') {
      return pathSegments[2];
    }
    if(cancelUrl) {
      const url = new URL(cancelUrl);
      const pathSegments = url.pathname.split('/');
      if (pathSegments.length >= 3 && pathSegments[1] === 'cancellations') {
        return pathSegments[2];
      }
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Generate Calendly iCal/Outlook URL
 */
function getCalendlyICalUrl(appointment: CalendlyAppointment): string {
  // Try to get eventId from reschedule URL first, fallback to userCalendarId
  const eventId = appointment.rescheduleUrl
    ? extractEventIdFromUrl(appointment.rescheduleUrl, appointment.cancelUrl)
    : null;

  const identifier = eventId || appointment.userCalendarId;
  return `https://calendly.com/invitees/${identifier}/ics`;
}

/**
 * Generate Calendly Google Calendar URL
 */
function getCalendlyGoogleCalendarUrl(appointment: CalendlyAppointment): string {
  // Try to get eventId from reschedule URL first, fallback to userCalendarId
  const eventId = appointment.rescheduleUrl
    ? extractEventIdFromUrl(appointment.rescheduleUrl, appointment.cancelUrl)
    : null;

  const identifier = eventId || appointment.userCalendarId;
  return `https://calendly.com/invitees/${identifier}/google`;
}

/**
 * Calendar Dropdown Component
 */
const CalendarDropdown: React.FC<{
  appointment: CalendlyAppointment;
}> = ({ appointment }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { isDark } = useTheme();

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const calendarOptions = [
    {
      name: 'Apple Calendar',
      onClick: () => window.open(getCalendlyICalUrl(appointment), '_blank'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      name: 'Google Calendar',
      onClick: () => window.open(getCalendlyGoogleCalendarUrl(appointment), '_blank'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      name: 'Outlook',
      onClick: () => window.open(getCalendlyICalUrl(appointment), '_blank'),
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
        </svg>
      )
    }
  ];

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="outline"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1"
      >
        Add to Calendar
      </Button>

      {isOpen && (
        <div className={`absolute top-0 left-0 w-48 rounded-md shadow-lg z-50 ${
          isDark ? 'bg-neutral-800 border border-neutral-700' : 'bg-white border border-neutral-200'
        }`}>
          <div className="py-1">
            {calendarOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => {
                  option.onClick();
                  setIsOpen(false);
                }}
                className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:${
                  isDark ? 'bg-neutral-700' : 'bg-neutral-50'
                } transition-colors`}
              >
                {option.icon}
                <span className="text-sm">{option.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Appointment Display Component
 */
export const AppointmentDisplay: React.FC<AppointmentDisplayProps> = ({
  appointment,
  isLoading,
  error,
  onRetry,
  showButtons = true,
  title = "Appointment Details"
}) => {
  const { isDark } = useTheme();

  // Loading state
  if (isLoading) {
    return (
      <div className="animate-pulse space-y-3 text-center">
        <div className="h-5 bg-neutral-300 rounded w-40"></div>
        <div className="space-y-2">
          <div className="h-3 bg-neutral-300 rounded w-32"></div>
          <div className="h-3 bg-neutral-300 rounded w-36"></div>
        </div>
        <div className="flex gap-2">
          <div className="h-6 bg-neutral-300 rounded w-16"></div>
          <div className="h-6 bg-neutral-300 rounded w-20"></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="space-y-3 text-center">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            isDark ? 'bg-error-900/20' : 'bg-error-50'
          }`}>
            <svg className={`w-3 h-3 ${
              isDark ? 'text-error-400' : 'text-error-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <BodyText size="sm" className="font-medium">Unable to Load Appointments</BodyText>
        </div>
        <BodyText variant="muted" size="sm">{error.message}</BodyText>
        {onRetry && (
          <Button variant="secondary" size="sm" onClick={onRetry}>
            Try Again
          </Button>
        )}
      </div>
    );
  }

  // No appointment state
  if (!appointment) {
    return (
      <div className="space-y-3 text-center">
        <div className="flex items-center gap-2">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
            isDark ? 'bg-primary-900/20' : 'bg-primary-50'
          }`}>
            <svg className={`w-3 h-3 ${
              isDark ? 'text-primary-400' : 'text-primary-600'
            }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <BodyText size="sm" className="font-medium">No Appointments Found</BodyText>
        </div>
        <BodyText variant="muted" size="sm">
          Schedule your next appointment below.
        </BodyText>
      </div>
    );
  }

  // Appointment details
  const { date, time, dayOfWeek } = formatAppointmentDateTime(appointment.startTime, appointment.endTime);
  const timeUntil = getTimeUntilAppointment(appointment.startTime);

  return (
    <div className="space-y-3 text-center">
      <div className="space-y-1 text-center">
        <SubtitleText>{title}</SubtitleText>

        {appointment.eventStatus === 'canceled' ? (
          <div className="flex items-center justify-center">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-800'
            }`}>
              Cancelled
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2 justify-center">
            <BodyText size="sm" className="font-medium">{dayOfWeek}</BodyText>
            <BodyText variant="muted" size="sm">•</BodyText>
            <BodyText variant="secondary" size="sm">{timeUntil}</BodyText>
          </div>
        )}

        <BodyText variant="secondary" size="sm">{date}</BodyText>
        <BodyText variant="secondary" size="sm">{time}</BodyText>

        {appointment.eventName && (
          <BodyText variant="muted" size="sm">{appointment.eventName}</BodyText>
        )}
      </div>

      {/* Fee guidance: after the details, before the add-to-calendar menu (design §4.6) */}
      <div className="text-left">
        <PaymentGuidance guidance={appointment.paymentGuidance ?? null} compact />
      </div>

      {showButtons && appointment.eventStatus !== 'canceled' && (
        <div className="flex gap-2 justify-center flex-wrap">
          <CalendarDropdown appointment={appointment} />
        </div>
      )}

    </div>
  );
};

export default AppointmentDisplay;